import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFetch } from "../../hooks/useFetch";
import { useRoomViewers } from "../../hooks/useRoomViewers";
import { fetchPublicModelsService, publicModelPreviewUrl } from "../../services/publicModelsService";
import { STORAGE } from "../../utils/headers";
import { genderLabel } from "../../utils/labels";
import noimg from "../../assets/img/noimg.jpg";
import "../../pages/public/public-models.css";

const PER_PAGE = 12;

const modelImage = (model) => (model.img ? `${STORAGE}/${model.img}` : noimg);

/**
 * Parrilla de modelos, común a la vitrina pública y al listado de salas.
 *
 * Eran dos pantallas con el mismo contenido: los mismos filtros, la misma
 * paginación y la misma tarjeta, escritas dos veces y divergiendo poco a poco.
 * Peor aún, la del listado de salas se servía de un endpoint autenticado que
 * devolvía la fila completa de cada modelo, así que construía la miniatura con
 * `thumbnails/live/{streaming_token}.jpg`: la clave de emisión de OBS viajaba
 * al navegador de cualquier cliente y quedaba en el HTML. Ahora ambas beben
 * del catálogo público, que sirve la miniatura por un proxy con el slug.
 *
 * `variant` solo cambia el encuadre:
 *   - `public`: catálogo completo, la tarjeta abre el perfil.
 *   - `live`:   arranca filtrado a "en vivo" y la tarjeta entra a la sala.
 */
export default function ModelCatalog({ variant = "public" }) {
  const isLive = variant === "live";
  const navigate = useNavigate();

  // El filtro vive en la URL: así "en vivo" es un enlace que se puede
  // compartir y el botón de atrás del navegador deshace el filtro en vez de
  // sacarte de la página.
  const [searchParams, setSearchParams] = useSearchParams();

  const status = searchParams.get("estado") ?? (isLive ? "online" : "");
  const gender = searchParams.get("genero") ?? "";
  const search = searchParams.get("q") ?? "";
  const ageMin = searchParams.get("edad_min") ?? "";
  const ageMax = searchParams.get("edad_max") ?? "";
  const page = Number(searchParams.get("pagina") ?? 1);

  const [searchInput, setSearchInput] = useState(search);

  // Debounce del buscador: evita una petición por cada tecla.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) setParam({ q: searchInput, pagina: 1 });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // El buscador se sincroniza si la URL cambia por fuera (atrás, un enlace).
  useEffect(() => setSearchInput(search), [search]);

  const setParam = (changes) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(changes).forEach(([key, value]) => {
          if (value === "" || value === null || value === undefined || value === 1) {
            next.delete(key);
          } else {
            next.set(key, String(value));
          }
        });
        return next;
      },
      { replace: true }
    );
  };

  const filters = useMemo(
    () => ({
      search,
      gender,
      status,
      age_min: ageMin,
      age_max: ageMax,
      page,
      per_page: PER_PAGE,
    }),
    [search, gender, status, ageMin, ageMax, page]
  );

  const { data: models, pagination, loading, error } = useFetch(fetchPublicModelsService, {
    params: filters,
  });

  // Aforo real de las salas de esta página, solo donde se muestran salas.
  const roomIds = useMemo(
    () => (isLive ? models?.map((model) => model.id) ?? [] : []),
    [isLive, models]
  );
  const { countFor } = useRoomViewers(roomIds);

  // Ventana de paginación: como máximo 5 números, sin desbordar la fila.
  const pageWindow = useMemo(() => {
    const total = pagination?.total_pages || 1;
    const current = pagination?.current_page || 1;
    const start = Math.max(1, Math.min(current - 2, total - 4));
    const end = Math.min(total, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [pagination]);

  const goToPage = (next) => {
    if (next < 1 || next > (pagination?.total_pages || 1)) return;
    setParam({ pagina: next });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearFilters = () => setSearchParams({}, { replace: true });

  const hasFilters = Boolean(search || gender || ageMin || ageMax) || status !== (isLive ? "online" : "");

  const destination = (model) => (isLive ? `/live/${model.id}` : `/modelo/${model.slug}`);

  return (
    <section className="public-catalog">
      <div className="container">
        <header className="public-catalog-header">
          <h1 className="public-title">{isLive ? "Salas en vivo" : "Nuestras Modelos"}</h1>
          <div className="section-title-divider"></div>
          <p className="public-subtitle">
            {isLive
              ? "Entra a una transmisión en curso. Cambia el estado a \"Todas\" para ver también quién no está emitiendo ahora."
              : "Conoce sus perfiles, fotos y videos. Entrar es gratis; para el show en vivo solo necesitas crear tu cuenta."}
          </p>
        </header>

        <div className="public-filters">
          <div className="public-search">
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Buscar modelos"
            />
          </div>

          <select
            value={gender}
            onChange={(e) => setParam({ genero: e.target.value, pagina: 1 })}
            aria-label="Filtrar por género"
          >
            <option value="">Todos los géneros</option>
            <option value="female">Mujer</option>
            <option value="male">Hombre</option>
            <option value="trans">Trans</option>
            <option value="other">Otro</option>
          </select>

          <select
            value={status}
            onChange={(e) => setParam({ estado: e.target.value, pagina: 1 })}
            aria-label="Filtrar por estado"
          >
            <option value="online">En vivo ahora</option>
            <option value="">Todas</option>
            <option value="offline">Fuera de línea</option>
          </select>

          {/* El rango de edad solo estaba en el listado de salas; es igual de
              útil en la vitrina y ahora lo tienen las dos. */}
          <div className="public-age-range">
            <input
              type="number"
              min="18"
              max="99"
              placeholder="Mín."
              value={ageMin}
              onChange={(e) => setParam({ edad_min: e.target.value, pagina: 1 })}
              aria-label="Edad mínima"
            />
            <span aria-hidden="true">–</span>
            <input
              type="number"
              min="18"
              max="99"
              placeholder="Máx."
              value={ageMax}
              onChange={(e) => setParam({ edad_max: e.target.value, pagina: 1 })}
              aria-label="Edad máxima"
            />
          </div>

          {hasFilters && (
            <button type="button" className="public-clear-filters" onClick={clearFilters}>
              <i className="fa-solid fa-xmark" aria-hidden="true"></i> Limpiar
            </button>
          )}
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {/* La grilla conserva su alto durante la recarga para que la
            página no dé saltos al filtrar. */}
        <div className={`public-grid ${loading ? "is-loading" : ""}`}>
          {loading && !models
            ? Array.from({ length: PER_PAGE }).map((_, index) => (
                <div className="public-card skeleton" key={`skeleton-${index}`}>
                  <div className="public-card-media" />
                  <div className="public-card-body">
                    <span className="skeleton-line" />
                    <span className="skeleton-line short" />
                  </div>
                </div>
              ))
            : models?.map((model) => (
                <Link to={destination(model)} className="public-card" key={model.id}>
                  <div className="public-card-media">
                    <img
                      src={model.is_live ? publicModelPreviewUrl(model.slug) : modelImage(model)}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = modelImage(model);
                      }}
                      alt={model.name}
                      loading="lazy"
                      width="400"
                      height="300"
                    />
                    {model.is_live && (
                      <span className="public-live-badge">
                        <span className="pulse-dot"></span> EN VIVO
                      </span>
                    )}

                    {/* Mientras no se sepa el aforo no se enseña nada: un cero
                        sin comprobar es tan falso como inventarlo. */}
                    {isLive && countFor(model.id) !== undefined && (
                      <span className="public-card-viewers">
                        <i className="fa-solid fa-eye" aria-hidden="true"></i> {countFor(model.id)}
                      </span>
                    )}

                    <span className="public-card-counts">
                      <i className="fa-solid fa-image" aria-hidden="true"></i> {model.images_count ?? 0}
                      <i className="fa-solid fa-film ms-2" aria-hidden="true"></i> {model.videos_count ?? 0}
                    </span>

                    {isLive && (
                      <span className="public-card-play" aria-hidden="true">
                        <i className="fa-solid fa-play"></i>
                      </span>
                    )}
                  </div>
                  <div className="public-card-body">
                    <h2 className="public-card-name">{model.name}</h2>
                    <p className="public-card-meta">
                      {[genderLabel(model.gender), model.age && `${model.age} años`, model.city]
                        .filter(Boolean)
                        .join(" • ") || "Perfil disponible"}
                    </p>
                  </div>

                  {/* Desde la sala se llega al perfil y al revés: son dos caras
                      de la misma modelo y antes había que volver al menú. */}
                  {isLive && model.slug && (
                    <button
                      type="button"
                      className="public-card-profile"
                      title={`Ver el perfil de ${model.name}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/modelo/${model.slug}`);
                      }}
                    >
                      <i className="fa-solid fa-id-badge" aria-hidden="true"></i>
                    </button>
                  )}
                </Link>
              ))}
        </div>

        {!loading && models?.length === 0 && (
          <div className="public-empty">
            <i className={`fa-solid ${isLive ? "fa-tower-broadcast" : "fa-user-slash"}`} aria-hidden="true"></i>
            <p>
              {isLive && status === "online"
                ? "Ahora mismo no hay nadie emitiendo."
                : "No encontramos modelos con estos filtros."}
            </p>
            {isLive && status === "online" && (
              <button type="button" className="public-btn public-btn-ghost" onClick={() => setParam({ estado: "" })}>
                Ver todas las modelos
              </button>
            )}
          </div>
        )}

        {pagination && pagination.total_pages > 1 && (
          <nav className="public-pagination" aria-label="Paginación de modelos">
            <button
              onClick={() => goToPage(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              aria-label="Página anterior"
            >
              <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
            </button>
            {pageWindow.map((item) => (
              <button
                key={item}
                className={item === pagination.current_page ? "active" : ""}
                onClick={() => goToPage(item)}
              >
                {item}
              </button>
            ))}
            <button
              onClick={() => goToPage(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.total_pages}
              aria-label="Página siguiente"
            >
              <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
            </button>
          </nav>
        )}
      </div>
    </section>
  );
}
